import React, { useMemo } from "react";
import {
  Operators,
  HysteresisMode,
  Rule,
  RulesSpec,
} from "../../../../features/notifications/notificationSlice";
import { CiCircleRemove } from "react-icons/ci";
import { useAppSelector } from "../../../../app/hooks";
import {
  Device,
  makeSelectDevicesByGroupId,
} from "../../../../features/devices/deviceSlice";
import { useParams } from "react-router-dom";
import { ruleSpecKeys, extractDeviceParameters } from "../utils";
import { MdOutlineError } from "react-icons/md";
import { LanguageText } from "../../../../dictionary/GroupNotificationText";
import useGetLanguage from "../../../../utils/useGetLanguage";

type specKeys = keyof RulesSpec;
interface NotificationRuleProps {
  rule: Rule;
  onRuleSpecChange: (ruleID: string, ruleKey: string, ruleValue: any) => void;
  onRemoveRule: (ruleID: string) => void;
  error?: boolean;
}

const NotificationRule = ({
  rule,
  onRuleSpecChange,
  onRemoveRule,
  error,
}: NotificationRuleProps): JSX.Element => {
  const { groupid } = useParams<{
    groupid: string;
  }>();
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupNotificationText",
  });

  const selectDevicesByGroupId = useMemo(makeSelectDevicesByGroupId, []);
  const groupDevices = useAppSelector((state) => selectDevicesByGroupId(state, groupid));

  const currentDevice = useMemo(() => {
    if (!rule?.spec?.device_eui) return undefined;
    return groupDevices.find((device) => device.eui === rule?.spec?.device_eui);
  }, [groupDevices, rule?.spec?.device_eui]);

  const parameters = useMemo(() => {
    return extractDeviceParameters(currentDevice || null);
  }, [currentDevice]);

  const isSwitchParam = rule?.spec?.parameter?.startsWith("sw_");

  const handleSpecChange = (
    event: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, name } = event.target;
    const numericKeys: string[] = [
      ruleSpecKeys.operator,
      ruleSpecKeys.threshold,
      ruleSpecKeys.hysteresisMode,
      ruleSpecKeys.hysteresisValue,
    ];

    if (numericKeys.includes(name)) {
      // Early return to allow empty string(clear) or minus (as a first character)
      if (value === "" || value === "-") {
        onRuleSpecChange(rule.id || "", name, value);
        return;
      }

      const validNumericValue = /^-?$|^-?(0|[1-9]\d*)$/;
      if (validNumericValue.test(value)) {
        onRuleSpecChange(rule.id || "", name, Number(value));
      } else {
        // Clear on invalid input (e.g. leading zeros)
        onRuleSpecChange(rule.id || "", name, "");
      }
    } else {
      onRuleSpecChange(rule.id || "", name, value);
    }
  };

  const operators = [
    {
      optionName: Language?.selectOperator,
      optionValue: Operators.DF,
    },
    {
      optionName: `${Language?.lessThan} (<)`,
      optionValue: Operators.LT,
    },
    {
      optionName: `${Language?.lessThanOrEqualTo} (<=)`,
      optionValue: Operators.LTE,
    },
    {
      optionName: `${Language?.equalTo} (=)`,
      optionValue: Operators.EQ,
    },
    {
      optionName: `${Language?.notEqualTo} (!=)`,
      optionValue: Operators.NEQ,
    },
    {
      optionName: `${Language?.greaterThan} (>)`,
      optionValue: Operators.GT,
    },
    {
      optionName: `${Language?.greaterThanOrEqualTo} (>=)`,
      optionValue: Operators.GTE,
    },
    {
      optionName: Language?.open,
      optionValue: Operators.OPEN,
    },
    {
      optionName: Language?.close,
      optionValue: Operators.CLOSED,
    },
    {
      optionName: Language?.either,
      optionValue: Operators.EITHER,
    },
  ];

  const hysteresisModeOptions = [
    {
      name: Language?.none,
      value: HysteresisMode.NONE,
    },
    {
      name: Language?.absolute,
      value: HysteresisMode.ABSOLUTE,
    },
    {
      name: Language?.percentage,
      value: HysteresisMode.PERCENTAGE,
    },
  ];

  const filteredOperators = operators.filter((op) => {
    // Always keep the default option)
    if (op.optionValue === Operators.DF) return true;
    if (isSwitchParam) {
      return (
        op.optionValue === Operators.OPEN ||
        op.optionValue === Operators.CLOSED ||
        op.optionValue === Operators.EITHER
      );
    } else {
      return !(
        op.optionValue === Operators.OPEN ||
        op.optionValue === Operators.CLOSED ||
        op.optionValue === Operators.EITHER
      );
    }
  });

  return (
    <div className="relative mb-6 p-4 sm:p-6 lg:p-8 rounded-lg bg-gray-100 shadow-md border border-gray-300 overflow-hidden">
      <CiCircleRemove
        size={22}
        color={"#e53e3e"}
        className="absolute top-3 right-3 sm:top-3 sm:right-3 cursor-pointer hover:text-red-500 transition-colors"
        onClick={() => onRemoveRule(rule?.id || "")}
      />
      <div className="mt-8 sm:mt-10">
        {/* Device Selector */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full mb-4">
          <select
            className="border border-gray-300 rounded-md p-2 w-full text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
            id="deviceEUI"
            name={ruleSpecKeys.device_eui}
            value={rule.spec?.device_eui}
            onChange={handleSpecChange}
          >
            <option value="">{Language?.selectDevice}</option>
            {groupDevices.length ? (
              groupDevices.map((device: Device) => (
                <option key={device.id} value={device.eui}>
                  {device.name}
                </option>
              ))
            ) : (
              <option value="">{Language?.noDeviceFound}</option>
            )}
          </select>
        </div>

        {rule.spec?.device_eui && (
          <div className="flex flex-wrap items-center w-full gap-2 sm:gap-3">
            <label className="text-gray-600 font-semibold text-sm whitespace-nowrap">
              {Language?.if}
            </label>
            <select
              className="border border-gray-300 rounded-md p-2 flex-1 min-w-[120px] text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
              name={ruleSpecKeys.parameter}
              value={rule.spec?.parameter}
              onChange={handleSpecChange}
            >
              <option value="">{Language?.selectParameter}</option>
              {parameters?.length &&
                parameters.map((parameter: any) => (
                  <option key={parameter?.id} value={parameter.id}>
                    {parameter.description}
                  </option>
                ))}
            </select>

            {/* Operator */}
            {rule?.spec?.parameter && (
              <select
                className="border border-gray-300 rounded-md p-2 text-sm flex-1 min-w-[90px] focus:ring-2 focus:ring-blue-300"
                name={ruleSpecKeys.operator}
                value={rule.spec?.operator}
                onChange={handleSpecChange}
              >
                {filteredOperators.map((operator, i: number) => (
                  <option key={i} value={operator.optionValue}>
                    {operator.optionName}
                  </option>
                ))}
              </select>
            )}

            {/* Threshold */}
            {rule?.spec?.parameter &&
              !isSwitchParam &&
              rule.spec.operator !== Operators.DF && (
                <input
                  type="text"
                  name={ruleSpecKeys.threshold}
                  value={rule.spec?.threshold}
                  onChange={handleSpecChange}
                  placeholder="Threshold"
                  className="border border-gray-300 rounded-md p-2 text-sm flex-1 min-w-[80px] focus:ring-2 focus:ring-blue-300"
                />
              )}

            {/* Hysteresis Mode + Value */}
            {rule?.spec?.parameter &&
              !isSwitchParam &&
              rule.spec.operator !== Operators.DF && (
                <div
                  key="hysteresis"
                  className="flex flex-wrap items-center gap-2 w-full sm:w-auto"
                >
                  <span className="text-gray-600 text-sm whitespace-nowrap">
                    {Language?.hysteresis}
                  </span>
                  <select
                    key="hysteresis-mode"
                    name={ruleSpecKeys.hysteresisMode}
                    value={rule.spec?.hysteresis?.mode}
                    onChange={handleSpecChange}
                    className="border border-gray-300 rounded-md p-2 text-sm flex-1 min-w-[90px] focus:ring-2 focus:ring-blue-300"
                  >
                    {hysteresisModeOptions.map((hysteresis) => (
                      <option key={hysteresis.name} value={hysteresis.value}>
                        {hysteresis.name}
                      </option>
                    ))}
                  </select>
                  {rule.spec?.hysteresis?.mode !== HysteresisMode.NONE && (
                    <input
                      key="hysteresis-value"
                      type="text"
                      name={ruleSpecKeys.hysteresisValue}
                      value={rule.spec?.hysteresis?.value ?? ""}
                      onChange={handleSpecChange}
                      placeholder="Value"
                      className="border border-gray-300 rounded-md p-2 text-sm flex-1 min-w-[70px] focus:ring-2 focus:ring-blue-300"
                    />
                  )}
                </div>
              )}
          </div>
        )}
      </div>
      {error && (
        <div className="flex items-center mt-3">
          <MdOutlineError className="text-red-500 text-lg mr-2" />
          <span className="text-xs text-red-500">{Language?.ruleErrorMsg}</span>
        </div>
      )}
    </div>
  );
};

export default NotificationRule;
